/**
 * Utility per risolvere contatto con fallback phone/email dai contatti del cliente.
 */

interface ContactCandidate {
  id: string;
  name: string;
  phone?: string | null;
  email?: string | null;
  role?: string | null;
  is_referente?: boolean;
  is_primary?: boolean;
}

interface ResolvedContactInfo {
  id: string;
  name: string;
  phone?: string;
  email?: string;
  role?: string;
  source: 'deal' | 'client';
}

/**
 * Risolve il miglior contatto con fallback per phone/email.
 *
 * Se il primary contact esiste ma non ha phone/email, li cerca nei fallbackCandidates.
 * Se non c'è primary, sceglie il miglior candidato dai fallback.
 *
 * Priorità fallback: is_referente (con phone) > is_primary (con phone) > primo con phone > is_referente > is_primary > primo
 */
export function resolveContactInfo(
  primary: ContactCandidate | null | undefined,
  source: 'deal' | 'client',
  fallbackCandidates: ContactCandidate[]
): ResolvedContactInfo | undefined {
  // Se non c'è nulla, undefined
  if (!primary && fallbackCandidates.length === 0) return undefined;

  // Se non c'è primary, scegli il miglior candidato dai fallback
  if (!primary) {
    const best = pickBestContact(fallbackCandidates);
    if (!best) return undefined;
    return {
      id: best.id,
      name: best.name,
      phone: best.phone || undefined,
      email: best.email || undefined,
      role: best.role || undefined,
      source: 'client',
    };
  }

  // C'è un primary: usa i suoi dati, ma riempi phone/email da fallback se mancanti
  let phone = primary.phone || undefined;
  let email = primary.email || undefined;

  if (!phone || !email) {
    const fallback = pickBestContact(fallbackCandidates);
    if (fallback) {
      if (!phone) phone = fallback.phone || undefined;
      if (!email) email = fallback.email || undefined;
    }
  }

  return {
    id: primary.id,
    name: primary.name,
    phone,
    email,
    role: primary.role || undefined,
    source,
  };
}

/**
 * Sceglie il miglior contatto da una lista, preferendo:
 * 1. is_referente con phone
 * 2. is_primary con phone
 * 3. primo con phone
 * 4. is_referente (senza phone)
 * 5. is_primary (senza phone)
 * 6. primo contatto
 */
function pickBestContact(candidates: ContactCandidate[]): ContactCandidate | null {
  if (candidates.length === 0) return null;

  return (
    candidates.find(c => c.is_referente && c.phone) ||
    candidates.find(c => c.is_primary && c.phone) ||
    candidates.find(c => c.phone) ||
    candidates.find(c => c.is_referente) ||
    candidates.find(c => c.is_primary) ||
    candidates[0] ||
    null
  );
}

/**
 * Risolve il miglior phone/email da una lista di contatti con fallback.
 * Utile per ClientList e ContactsPage dove non c'è un "primary" da deal.
 *
 * @param directPhone - phone del client/lead (campo diretto)
 * @param directEmail - email del client/lead (campo diretto)
 * @param contacts - lista client_contacts
 * @returns { phone, email } con fallback applicati
 */
export function resolveClientContactFallback(
  directPhone: string | null | undefined,
  directEmail: string | null | undefined,
  contacts: ContactCandidate[]
): { phone?: string; email?: string; contactName?: string } {
  let phone = directPhone || undefined;
  let email = directEmail || undefined;
  let contactName: string | undefined;

  if (!phone || !email) {
    const referente = contacts.find(c => c.is_referente);
    const primary = contacts.find(c => c.is_primary);

    if (!phone) {
      const phoneSource = referente?.phone ? referente
        : primary?.phone ? primary
        : contacts.find(c => c.phone);
      if (phoneSource) {
        phone = phoneSource.phone || undefined;
        if (!contactName && phoneSource.name) contactName = phoneSource.name;
      }
    }
    if (!email) {
      const emailSource = referente?.email ? referente
        : primary?.email ? primary
        : contacts.find(c => c.email);
      if (emailSource) {
        email = emailSource.email || undefined;
        if (!contactName && emailSource.name) contactName = emailSource.name;
      }
    }
  }

  return { phone, email, contactName };
}
